import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, ExternalLink, Crown, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SUBSCRIPTION_TIERS, type SubscriptionTierKey } from "@/lib/subscription-tiers";
import { toast } from "sonner";
import { Link } from "@/lib/router-compat";

const CHECKOUT_URLS: Partial<Record<SubscriptionTierKey, string>> = {
  starter: "https://buy.stripe.com/28EaEW4Kifgrejq3fJ5J603",
  professional: "https://buy.stripe.com/cNi3cu0u2ecn1wE5nR5J604",
  agency: "https://buy.stripe.com/28E7sK1y65FR4IQ2bF5J605",
};

const ENTERPRISE_CONTACT_EMAIL = "sales@sentinelforgesystems.com";

// Cache the Stripe-customer check for 30 min — it rarely changes and the RPC
// used to run on every auth event for every user.
const HSC_CACHE_KEY = "sf_has_stripe_customer_v1";
const HSC_TTL_MS = 30 * 60 * 1000;

const Pricing = () => {
  const { user, subscription, refreshSubscription } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    // Read cache first
    try {
      const raw = localStorage.getItem(HSC_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { userId: string; value: boolean; ts: number };
        if (parsed.userId === user.id && Date.now() - parsed.ts < HSC_TTL_MS) {
          setHasStripeCustomer(parsed.value);
          return;
        }
      }
    } catch { /* ignore */ }
    // Fall back to RPC, then cache
    (async () => {
      try {
        const { data } = await (supabase.rpc as any)("has_stripe_customer");
        const val = data === true;
        setHasStripeCustomer(val);
        localStorage.setItem(HSC_CACHE_KEY, JSON.stringify({ userId: user.id, value: val, ts: Date.now() }));
      } catch {
        setHasStripeCustomer(false);
      }
    })();
  }, [user]);


  const handleManageSubscription = async () => {
    if (!hasStripeCustomer) {
      toast.info("Your plan was granted manually — there's no billing to manage.");
      return;
    }
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to open subscription management");
    } finally {
      setPortalLoading(false);
    }
  };

  const tiers = (Object.entries(SUBSCRIPTION_TIERS) as [SubscriptionTierKey, typeof SUBSCRIPTION_TIERS[SubscriptionTierKey]][])
    .filter(([key, tier]) => !('internal' in tier && tier.internal) || key === 'enterprise');

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Choose Your Plan</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Scale your security proposal operations with the right plan for your business.
          </p>
          {subscription.subscribed && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Badge variant="outline" className="border-primary text-primary">
                <Crown className="h-3 w-3 mr-1" />
                {subscription.tier ? SUBSCRIPTION_TIERS[subscription.tier].name : "Active"} Plan
              </Badge>
              <Button variant="outline" size="sm" onClick={handleManageSubscription} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                Manage Subscription
              </Button>
              <Button variant="ghost" size="sm" onClick={() => refreshSubscription()}>
                Refresh Status
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {tiers.map(([key, tier]) => {
            const isCurrentPlan = subscription.tier === key;
            const isProfessional = key === "professional";
            const isEnterprise = key === "enterprise";

            return (
              <Card
                key={key}
                className={`relative border-border bg-card shadow-card flex flex-col ${
                  isProfessional ? "ring-2 ring-primary" : ""
                } ${isCurrentPlan ? "border-primary" : ""} ${isEnterprise ? "border-amber-500/50 bg-gradient-to-b from-amber-500/5 to-transparent" : ""}`}
              >
                {isProfessional && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                  </div>
                )}
                {isEnterprise && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[hsl(38,92%,50%)] text-white">
                      <Building2 className="h-3 w-3 mr-1" />
                      Enterprise
                    </Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="outline" className="border-primary text-primary bg-background">Your Plan</Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-foreground flex items-center gap-2">
                    {tier.name}
                    {isEnterprise && <Building2 className="h-4 w-4 text-[hsl(38,92%,50%)]" />}
                  </CardTitle>
                  <CardDescription>
                    {isEnterprise ? (
                      <span className="text-2xl font-bold text-foreground">Custom Pricing</span>
                    ) : tier.price !== null ? (
                      <span className="text-3xl font-bold text-foreground">${tier.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                    ) : (
                      <span className="text-3xl font-bold text-foreground">Custom</span>
                    )}
                  </CardDescription>
                  {'description' in tier && tier.description && (
                    <p className="text-sm text-muted-foreground mt-2">{tier.description}</p>
                  )}
                </CardHeader>
                <CardContent className="flex-1">
                  {'features' in tier && tier.features && (
                    <ul className="space-y-2">
                      {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className={`h-4 w-4 mt-0.5 shrink-0 ${isEnterprise ? "text-[hsl(38,92%,50%)]" : "text-primary"}`} />
                        {feature}
                      </li>
                      ))}
                    </ul>
                  )}
                  {isEnterprise && 'pricingFactors' in tier && tier.pricingFactors && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm font-medium text-foreground mb-2">Pricing based on:</p>
                      <ul className="space-y-1">
                        {tier.pricingFactors.map((factor) => (
                      <li key={factor} className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(38,92%,50%)]/60" />
                        {factor}
                      </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
                <CardFooter>
                  {isCurrentPlan ? (
                    <Button className="w-full" variant="outline" disabled>
                      Current Plan
                    </Button>
                  ) : isEnterprise ? (
                    <Button 
                      className="w-full bg-[hsl(38,92%,50%)] hover:bg-[hsl(38,92%,45%)] text-white" 
                      asChild
                    >
                      <a href={`mailto:${ENTERPRISE_CONTACT_EMAIL}`}>Contact Sales</a>
                    </Button>
                  ) : CHECKOUT_URLS[key] ? (
                    !user ? (
                      <Button
                        className={`w-full ${isProfessional ? "gradient-primary text-primary-foreground shadow-glow hover:opacity-90" : ""}`}
                        variant={isProfessional ? "default" : "outline"}
                        asChild
                      >
                        <Link to="/auth?signup">Create Account to Subscribe</Link>
                      </Button>
                    ) : (
                      <Button
                        className={`w-full ${isProfessional ? "gradient-primary text-primary-foreground shadow-glow hover:opacity-90" : ""}`}
                        variant={isProfessional ? "default" : "outline"}
                        asChild
                      >
                        <a href={CHECKOUT_URLS[key]} target="_blank" rel="noopener noreferrer">
                          {subscription.subscribed ? "Switch Plan" : "Get Started"}
                        </a>
                      </Button>
                    )
                  ) : (
                    <Button className="w-full" variant="outline" asChild>
                      <a href={`mailto:${ENTERPRISE_CONTACT_EMAIL}`}>Contact Sales</a>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
};

export default Pricing;
